import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';
import { FaCheckCircle, FaDownload, FaChartPie, FaBriefcase, FaUsers, FaServer } from 'react-icons/fa';
import BrandText from '../../styles/brandStyles';

const ErpEnterprise = () => {
  return (
    <>
      <SEO 
        title="OPUS - Módulo 6: ERP Enterprise | ECOSOFT"
        description="Sistema integral de gestión empresarial diseñado específicamente para empresas de la industria de la construcción."
        keywords="OPUS, ERP, gestión empresarial, construcción, productividad, ventas, operaciones"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#1A2B39]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-12">
              <div className="text-white mb-2">Módulo 6</div>
              <h1 className="text-4xl font-bold text-white mb-6">
                <BrandText>OPUS ERP</BrandText> ENTERPRISE
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                La solución integral para la gestión empresarial en la construcción. Unifica las operaciones, finanzas y recursos humanos en una sola plataforma.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/soporte/descargas/opus24" 
                  className="px-6 py-3 bg-[#0066CC] hover:bg-[#0088FF] text-white font-medium rounded-md flex items-center"
                >
                  <FaDownload className="mr-2" /> Descargar
                </Link>
                <Link 
                  to="/demo" 
                  className="px-6 py-3 border border-white hover:bg-white hover:text-[#1A2B39] text-white font-medium rounded-md transition-colors"
                >
                  Solicitar demostración
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <img 
                src="/img/modulos/erp-enterprise.png" 
                alt="OPUS ERP Enterprise" 
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Características principales</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartPie className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Gestión financiera integral</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Contabilidad general y analítica</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Cuentas por cobrar y por pagar</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Cumplimiento fiscal automatizado</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Flujo de efectivo y presupuestos</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Reportes financieros personalizados</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBriefcase className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Operaciones y proyectos</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Gestión integral de proyectos</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Control de costos de obra</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Seguimiento de rentabilidad</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Integración con presupuestos</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Administración de subcontratistas</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Recursos humanos</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0055B8] mt-1 mr-2 flex-shrink-0" />
                  <span>Nómina y administración de personal</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0055B8] mt-1 mr-2 flex-shrink-0" />
                  <span>Gestión de listas de raya</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0055B8] mt-1 mr-2 flex-shrink-0" />
                  <span>Control de asistencia y productividad</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0055B8] mt-1 mr-2 flex-shrink-0" />
                  <span>Administración de documentación</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-[#0055B8] mt-1 mr-2 flex-shrink-0" />
                  <span>Cumplimiento de obligaciones laborales</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Beneficios de OPUS ERP</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#F5F7FA] p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-[#0055B8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaServer className="text-[#0055B8] text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-2">Integración total</h3>
              <p className="text-gray-600">
                Todos los datos y procesos en un solo sistema unificado, eliminando silos de información.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-[#0055B8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartPie className="text-[#0055B8] text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-2">Visibilidad en tiempo real</h3>
              <p className="text-gray-600">
                Información actualizada de todos los aspectos de su negocio para tomar decisiones informadas.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-[#0055B8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-[#0055B8] text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-2">Automatización</h3>
              <p className="text-gray-600">
                Reducción de tareas manuales y eliminación de duplicación de trabajo.
              </p>
            </div>
            
            <div className="bg-[#F5F7FA] p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-[#0055B8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-[#0055B8] text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-2">Colaboración mejorada</h3>
              <p className="text-gray-600">
                Facilita la comunicación entre departamentos y equipos de proyecto.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Integration Section */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1A2B39]">Integración con otros módulos</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            OPUS ERP Enterprise se integra perfectamente con todos los módulos de OPUS para una solución completa
          </p>
          
          <div className="grid md:grid-cols-6 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 1</h3>
              <p className="text-gray-600 text-sm">Presupuesto Programable</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 2</h3>
              <p className="text-gray-600 text-sm">Planeación y Control</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 3</h3>
              <p className="text-gray-600 text-sm">Administración de Compras</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 4</h3>
              <p className="text-gray-600 text-sm">Dependencias</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 5</h3>
              <p className="text-gray-600 text-sm">Cuantificación BIM</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center border-2 border-[#0055B8]">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Módulo 6</h3>
              <p className="text-gray-600 text-sm">ERP Enterprise</p>
              <div className="mt-3 h-1 w-full bg-[#0055B8] rounded"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-[#1A2B39]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">Transforme su gestión empresarial</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Optimice todos los procesos de su empresa con OPUS ERP Enterprise
          </p>
          <div className="flex justify-center flex-wrap gap-4">
            <Link 
              to="/comprar/opus-m6" 
              className="px-8 py-3 bg-[#0055B8] hover:bg-[#0066CC] text-white font-medium rounded-md"
            >
              Adquirir ahora
            </Link>
            <Link 
              to="/soporte/descargas/opus24" 
              className="px-8 py-3 border border-white hover:bg-white hover:text-[#1A2B39] text-white font-medium rounded-md transition-colors"
            >
              Descargar versión de prueba
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ErpEnterprise;
